import React from 'react';
import PropTypes from 'prop-types';
import {
  Badge, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  beerDisplayName,
  beerSystemLinked,
  missingPlatformLabels,
  PLATFORM_SYSTEMS,
  skuFormatLabel,
  skuIssueCounts,
  skuSystemCounts,
  SYSTEM_LABELS,
} from './helpers';

const { Text } = Typography;

/** OK / N/A / red badge for one system at beer level. */
function SystemCountCell({ count, linked }) {
  if (!linked) return <Tag color="default">N/A</Tag>;
  if (!count) return <Tag color="success">OK</Tag>;
  return <Badge count={count} overflowCount={999} style={{ backgroundColor: '#cf1322' }} />;
}

SystemCountCell.propTypes = {
  count: PropTypes.number,
  linked: PropTypes.bool,
};

SystemCountCell.defaultProps = {
  count: 0,
  linked: false,
};

function skuTooltip(sku) {
  const issues = skuIssueCounts(sku);
  const counts = skuSystemCounts(sku);
  const bits = PLATFORM_SYSTEMS
    .filter((s) => counts[s] > 0)
    .map((s) => `${SYSTEM_LABELS[s]} ${counts[s]}`);
  if (issues.missing > 0) bits.push(`Missing ${issues.missing}`);
  if (issues.misalignment > 0) bits.push(`Misalignment ${issues.misalignment}`);
  if (issues.kountaCost > 0) bits.push(`Kounta cost ${issues.kountaCost}`);
  const head = sku.skuName || `SKU ${sku.skuKey}`;
  if (bits.length === 0) return `${head} — no discrepancies`;
  return `${head} — ${bits.join(', ')}`;
}

function SkuChips({ beer, missingLinksMode }) {
  const skus = beer.skus || [];
  if (skus.length === 0) return <Text type="secondary">—</Text>;
  return (
    <span>
      {skus.map((sku, i) => {
        const issues = skuIssueCounts(sku);
        const linkGaps = missingLinksMode ? missingPlatformLabels(sku) : [];
        let color = 'default';
        let suffix = '';
        if (missingLinksMode && linkGaps.length > 0) {
          color = 'orange';
        } else if (issues.discrepancy > 0) {
          color = 'red';
          suffix = ` (${issues.discrepancy})`;
        } else if (issues.missing > 0) {
          color = 'orange';
          suffix = ` (${issues.missing})`;
        } else if (issues.misalignment > 0) {
          color = 'green';
          suffix = ` (${issues.misalignment})`;
        }
        const label = (beer.formats && beer.formats.length === skus.length && beer.formats[i])
          || skuFormatLabel(sku);
        const title = missingLinksMode && linkGaps.length > 0
          ? `${sku.skuName || `SKU ${sku.skuKey}`} — ${linkGaps.join(', ')}`
          : skuTooltip(sku);
        return (
          <Tooltip key={sku.skuKey} title={title}>
            <span>
              <Tag color={color} style={{ marginBottom: 4 }}>
                {label}
                {suffix}
              </Tag>
              {sku.isLegacy ? (
                <Tag color="default" style={{ marginBottom: 4 }}>Legacy</Tag>
              ) : null}
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}

SkuChips.propTypes = {
  beer: PropTypes.shape({
    skus: PropTypes.arrayOf(PropTypes.shape({
      skuKey: PropTypes.number,
      skuName: PropTypes.string,
      totalDiscrepancies: PropTypes.number,
      isLegacy: PropTypes.bool,
    })),
    formats: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  missingLinksMode: PropTypes.bool,
};

SkuChips.defaultProps = {
  missingLinksMode: false,
};

function beerMeta(beer) {
  return [
    beer.productAbv != null && beer.productAbv !== '' ? `${beer.productAbv}%` : null,
    beer.productType || null,
    beer.productRange || null,
  ].filter(Boolean).join(' · ');
}

function MissingLinksCell({ beer }) {
  const labels = [...new Set(
    (beer.skus || []).flatMap((sku) => missingPlatformLabels(sku)),
  )];
  if (labels.length === 0) return <Text type="secondary">—</Text>;
  return (
    <span>
      {labels.map((label) => (
        <Tag key={label} color="warning" style={{ marginBottom: 4 }}>{label}</Tag>
      ))}
    </span>
  );
}

MissingLinksCell.propTypes = {
  beer: PropTypes.shape({
    skus: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
};

/**
 * One row per beer (product_matrix_id). Click a row to open the resolver drawer.
 */
export default function BeerTable({
  beers, loading, onOpen, showMissing, showMisalignment, missingLinksMode,
}) {
  const systemColumn = (sys) => ({
    title: SYSTEM_LABELS[sys],
    key: `status-${sys}`,
    width: 90,
    render: (_, beer) => (
      <SystemCountCell
        count={beer.counts?.[sys] || 0}
        linked={beerSystemLinked(beer, sys)}
      />
    ),
  });

  const columns = [
    {
      title: 'Beer',
      key: 'beer',
      ellipsis: true,
      render: (_, beer) => (
        <div>
          <Text strong>{beerDisplayName(beer)}</Text>
          {beerMeta(beer) ? (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>{beerMeta(beer)}</Text>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'SKUs',
      key: 'skus',
      width: 360,
      render: (_, beer) => <SkuChips beer={beer} missingLinksMode={missingLinksMode} />,
    },
    ...(missingLinksMode ? [{
      title: 'Missing links',
      key: 'missing-links',
      render: (_, beer) => <MissingLinksCell beer={beer} />,
    }] : []),
    ...PLATFORM_SYSTEMS.map(systemColumn),
    ...(showMissing ? [{
      title: 'Missing',
      key: 'missing',
      width: 90,
      render: (_, beer) => {
        const n = beer.counts?.missing || 0;
        if (!n) return <Tag>0</Tag>;
        return <Badge count={n} overflowCount={999} style={{ backgroundColor: '#d48806' }} />;
      },
    }] : []),
    ...(showMisalignment ? [{
      title: 'Misalignment',
      key: 'misalignment',
      width: 120,
      render: (_, beer) => {
        const n = beer.counts?.misalignment || 0;
        if (!n) return <Tag>0</Tag>;
        return <Badge count={n} overflowCount={999} style={{ backgroundColor: '#389e0d' }} />;
      },
    }] : []),
    {
      title: 'Total',
      key: 'total',
      width: 80,
      render: (_, beer) => beer.counts?.total || 0,
      sorter: (a, b) => (a.counts?.total || 0) - (b.counts?.total || 0),
    },
  ];

  return (
    <Table
      dataSource={beers}
      columns={columns}
      rowKey={(b) => (b.productMatrixId == null ? '__unassigned__' : String(b.productMatrixId))}
      loading={loading}
      pagination={{ pageSize: 50, showSizeChanger: true }}
      onRow={(beer) => ({
        onClick: () => onOpen(beer),
        style: { cursor: 'pointer' },
      })}
    />
  );
}

BeerTable.propTypes = {
  beers: PropTypes.arrayOf(PropTypes.shape({
    productMatrixId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productName: PropTypes.string,
    productAbv: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productType: PropTypes.string,
    productRange: PropTypes.string,
    skus: PropTypes.arrayOf(PropTypes.shape({})),
    counts: PropTypes.shape({
      guide: PropTypes.number,
      monday: PropTypes.number,
      cin7: PropTypes.number,
      shopify: PropTypes.number,
      kounta: PropTypes.number,
      total: PropTypes.number,
    }),
    formats: PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
  loading: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  showMissing: PropTypes.bool,
  showMisalignment: PropTypes.bool,
  missingLinksMode: PropTypes.bool,
};

BeerTable.defaultProps = {
  loading: false,
  showMissing: false,
  showMisalignment: false,
  missingLinksMode: false,
};
